// NEW MENU CARD NAVIGATION SYSTEM
class MenuCard {
    constructor() {
        this.currentPage = 1;
        this.totalPages = 17;
        this.isTransitioning = false;
        
        // Elements
        this.menuCard = null;
        this.menuPages = [];
        this.prevBtn = null;
        this.nextBtn = null;
        this.pageDots = [];
        this.pageCounter = null;
        this.contentArea = null;
        
        // Touch/Swipe properties
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchEndX = 0;
        this.touchEndY = 0;
        this.isScrolling = false;
        this.minSwipeDistance = 50;
        
        this.init();
    }
    
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupElements());
        } else {
            this.setupElements();
        }
    }
    
    setupElements() {
        console.log('🍕 Initializing New Menu Card System...');
        
        // Get DOM elements
        this.menuCard = document.querySelector('.menu-card');
        this.menuPages = Array.from(document.querySelectorAll('.menu-page-card'));
        this.prevBtn = document.getElementById('menuPrevBtn');
        this.nextBtn = document.getElementById('menuNextBtn');
        this.pageDots = Array.from(document.querySelectorAll('.page-dot'));
        this.pageCounter = document.querySelector('.current-page-num');
        this.contentArea = document.querySelector('.menu-content-area');
        
        if (!this.menuCard || this.menuPages.length === 0) {
            console.error('❌ Menu card elements not found');
            return;
        }
        
        console.log(`✅ Found ${this.menuPages.length} menu pages`);
        
        // Setup event listeners
        this.setupEventListeners();
        
        // Initialize first page
        this.showPage(1, false);
        
        console.log('✅ Menu Card System Ready!');
    }
    
    setupEventListeners() {
        // Button navigation
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.previousPage();
            });
        }
        
        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.nextPage();
            });
        }
        
        // No dot navigation anymore
        
        // Keyboard navigation
        document.addEventListener('keydown', (e) => {
            if (!this.isMenuVisible()) return;
            
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.previousPage();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.nextPage();
            } else if (e.key >= '1' && e.key <= '9') {
                e.preventDefault();
                const pageNum = parseInt(e.key);
                if (pageNum <= this.totalPages) {
                    this.showPage(pageNum);
                }
            }
        });
        
        // Touch/Swipe support
        if (this.contentArea) {
            this.setupTouchEvents();
        }
        
        // Resize handler for responsiveness
        window.addEventListener('resize', () => {
            this.handleResize();
        });
    }
    
    setupTouchEvents() {
        // Touch events for swipe navigation
        this.contentArea.addEventListener('touchstart', (e) => {
            this.touchStartX = e.touches[0].clientX;
            this.touchStartY = e.touches[0].clientY;
            this.isScrolling = false;
        }, { passive: true });
        
        this.contentArea.addEventListener('touchmove', (e) => {
            if (!this.isScrolling) {
                const currentX = e.touches[0].clientX;
                const currentY = e.touches[0].clientY;
                const diffX = Math.abs(currentX - this.touchStartX);
                const diffY = Math.abs(currentY - this.touchStartY);
                
                // Determine if this is a vertical scroll or horizontal swipe
                if (diffY > diffX) {
                    this.isScrolling = true;
                }
            }
        }, { passive: true });
        
        this.contentArea.addEventListener('touchend', (e) => {
            if (this.isScrolling) return;
            
            this.touchEndX = e.changedTouches[0].clientX;
            this.touchEndY = e.changedTouches[0].clientY;
            
            this.handleSwipe();
        }, { passive: true });
        
        // Prevent context menu on long press
        this.contentArea.addEventListener('contextmenu', (e) => {
            e.preventDefault();
        });
    }
    
    handleSwipe() {
        const diffX = this.touchStartX - this.touchEndX;
        const diffY = Math.abs(this.touchStartY - this.touchEndY);
        
        console.log('🔄 Swipe detected:', { diffX, diffY, minDistance: this.minSwipeDistance });
        
        // Only consider horizontal swipes (mehr tolerant)
        if (Math.abs(diffX) < this.minSwipeDistance || diffY > 100) {
            console.log('❌ Swipe ignored: too small or vertical');
            return;
        }
        
        if (diffX > 0) {
            // Swipe left - next page
            console.log('👉 Swipe left - next page');
            this.nextPage();
        } else {
            // Swipe right - previous page
            console.log('👈 Swipe right - previous page');
            this.previousPage();
        }
    }
    
    isMenuVisible() {
        if (!this.menuCard) return false;
        
        const rect = this.menuCard.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Check if menu card is in viewport
        return rect.top < windowHeight && rect.bottom > 0;
    }
    
    showPage(pageNum, animate = true) {
        if (pageNum < 1 || pageNum > this.totalPages || pageNum === this.currentPage) {
            return;
        }
        
        if (this.isTransitioning && animate) {
            return;
        }
        
        this.isTransitioning = true;
        
        console.log(`📄 Showing page ${pageNum}`);
        
        // Update current page
        const previousPage = this.currentPage;
        this.currentPage = pageNum;
        
        if (animate) {
            // Smooth transition - fade out current page
            const currentPageElement = this.menuPages[previousPage - 1];
            if (currentPageElement) {
                currentPageElement.style.opacity = '0';
                
                setTimeout(() => {
                    // Hide all pages
                    this.menuPages.forEach(page => {
                        page.classList.remove('active');
                        page.style.display = 'none';
                    });
                    
                    // Show target page
                    const targetPage = this.menuPages[pageNum - 1];
                    if (targetPage) {
                        targetPage.classList.add('active');
                        targetPage.style.display = 'grid';
                        targetPage.style.opacity = '0';
                        
                        // Fade in new page
                        setTimeout(() => {
                            targetPage.style.opacity = '1';
                        }, 50);
                    }
                    
                    // Update UI and reset transition
                    this.updateUI();
                    setTimeout(() => {
                        this.isTransitioning = false;
                    }, 800);
                    
                }, 400);
            }
        } else {
            // Instant switch
            this.menuPages.forEach(page => {
                page.classList.remove('active');
                page.style.display = 'none';
            });
            
            const targetPage = this.menuPages[pageNum - 1];
            if (targetPage) {
                targetPage.classList.add('active');
                targetPage.style.display = 'grid';
                targetPage.style.opacity = '1';
            }
            
            this.updateUI();
            this.isTransitioning = false;
        }
    }
    
    updateUI() {
        // Update page counter
        if (this.pageCounter) {
            this.pageCounter.textContent = this.currentPage;
        }
        
        // Update navigation buttons
        if (this.prevBtn) {
            this.prevBtn.disabled = this.currentPage === 1;
        }
        
        if (this.nextBtn) {
            this.nextBtn.disabled = this.currentPage === this.totalPages;
        }
        
        // No dots to update
    }
    
    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.showPage(this.currentPage + 1);
        }
    }
    
    previousPage() {
        if (this.currentPage > 1) {
            this.showPage(this.currentPage - 1);
        }
    }
    
    goToPage(pageNum) {
        this.showPage(pageNum);
    }
    
    handleResize() {
        // Handle responsive behavior
        const isMobile = window.innerWidth <= 768;
        
        if (isMobile) {
            // Adjust for mobile if needed
            this.contentArea?.classList.add('mobile');
        } else {
            this.contentArea?.classList.remove('mobile');
        }
    }
    
    // Public API methods
    getCurrentPage() {
        return this.currentPage;
    }
    
    getTotalPages() {
        return this.totalPages;
    }
    
    destroy() {
        // Clean up event listeners
        if (this.prevBtn) {
            this.prevBtn.removeEventListener('click', this.previousPage);
        }
        if (this.nextBtn) {
            this.nextBtn.removeEventListener('click', this.nextPage);
        }
        
        this.pageDots.forEach(dot => {
            dot.removeEventListener('click', this.goToPage);
        });
        
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('resize', this.handleResize);
        
        if (this.contentArea) {
            this.contentArea.removeEventListener('touchstart', this.handleTouchStart);
            this.contentArea.removeEventListener('touchmove', this.handleTouchMove);
            this.contentArea.removeEventListener('touchend', this.handleTouchEnd);
        }
    }
}

// Initialize menu card when DOM is ready
let menuCardInstance = null;

function initMenuCard() {
    if (menuCardInstance) {
        menuCardInstance.destroy();
    }
    menuCardInstance = new MenuCard();
}

// Auto-initialize
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMenuCard);
} else {
    initMenuCard();
}

// Export for debugging
window.menuCard = menuCardInstance;

// Global debug functions
window.menuCardDebug = {
    goToPage: (num) => menuCardInstance?.goToPage(num),
    getCurrentPage: () => menuCardInstance?.getCurrentPage(),
    nextPage: () => menuCardInstance?.nextPage(),
    previousPage: () => menuCardInstance?.previousPage()
};

console.log('🍕 New Menu Card JavaScript Loaded!');